import { Audience } from 'shared/models/auth';
import { FEDERAL_MODE } from 'config/toggles';
import DevsiteEnv from 'modules/core/DevsiteEnv';
import adminRoutes from './adminRoutes';
import consoleRoutes from './consoleRoutes';

// If a route changes:
// * Create a redirect (see `CLOUD_REDIRECT_RULES` in `LegacyRedirects.tsx`) so that user bookmarks remain operational
// * Update wcpcontent references to the new link
const home = '/';
const about = '/about';
const community = '/community';
const communityMvps = `${community}/developer-mvps`;
const communityEvents = `${community}/events`;
const communityLearningNetwork = `${community}/developer-learning-network`;
const adminTools = '/admin';
const adminApps = `${adminTools}${adminRoutes.apps}`;
const appBuilder = '/build';
const appCatalog = '/app-catalog';
const appDiff = (appRefId: string) => `${consoleApps}/diff/${appRefId}`;
const buildApp = (appRefId: string) => `${appBuilder}/${appRefId}`;
const buildLocalSuffix = '/local';
const consoleRoute = '/console';
const consoleApps = `${consoleRoute}${consoleRoutes.apps}`;
const consoleAppsMyApps = `${consoleApps}/my-apps`;
const consoleBuiltOnWorkdayApps = `${consoleApps}/builtOnWorkday`;
const consoleLicensedApps = `${consoleApps}/licensed`;
const consoleRetiredApps = `${consoleApps}/retired`;
const consoleAppRoot = `${consoleApps}/view`;
const consoleAppLicensedRoot = `${consoleApps}/licensed`;
const clients = `${consoleRoute}${consoleRoutes.apiClients}`;
const clientsMyClients = `${clients}/my-clients`;
const clientsUnassigned = `${clients}/unassigned`;
const clientView = (clientId: string) => `${clients}/view/${clientId}`;
const apiOverview = '/api-overview';
const documentation = '/documentation';
const orchestrationBuilder = `/orchestrate`;
const packageManagement = `${adminTools}${adminRoutes.packageManagement}`;
const packageManagementPermissions = `${packageManagement}${adminRoutes.permissions}`;
const accessPolicies = `${adminTools}/access-policies`;
const accessPoliciesProducts = `${accessPolicies}/products`;
const accessPoliciesFeatures = `${accessPolicies}/features`;
const accessPoliciesPackages = `${accessPolicies}/packages`;
const accessPoliciesPermissions = `${accessPolicies}/permissions`;
const search = '/search';
const terms = '/terms';
const tutorials = '/learn';
const sandbox = '/sandbox';

const routes = {
  home,
  adminTools,
  adminApps,
  adminAppsSearch: (search: string) => `${adminApps}?q=${search}`,
  adminAppView: (appRefId: string) => `${adminApps}/${appRefId}`,
  adminAppViewSource: (appRefId: string, versionId: string) => `${adminApps}/${appRefId}/activity/${versionId}/view`,
  adminAppViewDiff: (appRefId: string) => `${adminApps}/${appRefId}/diff`,
  adminCompany: `${adminTools}${adminRoutes.companies}`,
  adminCompanyEdit: (uuid: string) => `${adminTools}${adminRoutes.companies}/${uuid}/edit`,
  adminCompanyView: (uuid: string) => `${adminTools}${adminRoutes.companies}/${uuid}`,
  adminCompanyUsers: (uuid: string) => `${adminTools}${adminRoutes.companies}/${uuid}${adminRoutes.companyUsers}`,
  adminCompanyApps: (uuid: string) => `${adminTools}${adminRoutes.companies}/${uuid}${adminRoutes.apps}`,
  adminCompanyTenants: (uuid: string) => `${adminTools}${adminRoutes.companies}/${uuid}${adminRoutes.companyTenants}`,
  adminCompanyLicensedApps: (uuid: string) =>
    `${adminTools}${adminRoutes.companies}/${uuid}${adminRoutes.licensedApps}`,
  adminCompanySettings: (uuid: string) => `${adminTools}${adminRoutes.companies}/${uuid}${adminRoutes.settings}`,
  adminRoles: `${adminTools}${adminRoutes.roles}`,
  adminPermissions: `${adminTools}${adminRoutes.roles}${adminRoutes.permissions}`,
  adminUsers: `${adminTools}${adminRoutes.users}`,
  adminUsersExport: `${adminTools}${adminRoutes.usersExport}`,
  adminAppCatalog: `${adminTools}${adminRoutes.appCatalog}`,
  adminAppCatalogPreview: `${adminTools}${adminRoutes.appCatalog}/preview`,
  adminAppCatalogPreviewer: `${adminTools}${adminRoutes.appCatalog}/app-catalog-preview`,
  adminBuildConfig: `${adminTools}${adminRoutes.build}`,
  adminUtils: `${adminTools}${adminRoutes.build}/utils`,
  adminBuildEnv: `${adminTools}${adminRoutes.build}/env`,
  adminBuildMfe: `${adminTools}${adminRoutes.build}/mfe`,
  adminBuildRegions: `${adminTools}${adminRoutes.build}/regions`,
  apiOverview,
  apiExplorer: '/rest-api-explorer',
  apiDiscoveryCenter: '/copilot-apis',
  soapApiExplorer: '/soap-api-explorer',
  graphQLChangelog: '/graphapi-changelog',
  soapApiExplorerAuth: '/soap-api-explorer/auth',
  graphQLExplorer: '/graph-api-explorer',
  mcpExplorer: '/mcp-explorer',
  auth: '/auth',
  appBuilder,
  appDiff,
  buildApp,
  buildLocalSuffix,
  buildLocalApp: (appRefId: string) => `${appBuilder}${buildLocalSuffix}/${appRefId}`,
  buildLocalAppComponent: (appRefId: string, componentId: string) =>
    `${appBuilder}${buildLocalSuffix}/${appRefId}/${componentId}`,
  buildAppComponent: (appRefId: string, componentId: string) => `${buildApp(appRefId)}/${componentId}`,
  community,
  communityMvps,
  communityEvents,
  communityLearningNetwork,
  intro: '/intro',
  documentation,
  docView: (id: string) => `${documentation}/${id}`,
  downloads: FEDERAL_MODE ? about : '/downloads',
  developerAgent: FEDERAL_MODE ? about : '/developer-agent',
  narwhalAuth: '/orchestration-activity/auth',
  orchestrate: orchestrationBuilder,
  orchestrateAuth: `${orchestrationBuilder}/auth`,
  orchestrationWorkflows: (appId: string, promotionLevel: string, flowId: string) =>
    `${orchestrationBuilder}/app/${appId}/${promotionLevel}/workflows/${flowId}`,

  orchestrationsWithPromotion: (appId: string, promotionLevel: string) =>
    `/orchestrate/app/${appId}/${promotionLevel}/orchestrations`,
  /* CLOUD MODE (default): View a single orchestration */
  orchestration: (appId: string, promotionLevel: string, flowId: string) =>
    `${orchestrationBuilder}/app/${appId}/${promotionLevel}/orchestrations/${flowId}`,
  /* CLOUD MODE (default): View an app's orchestrations; redirects to first orchestration in app */
  orchestrateApp: (appId: string) => `/orchestrate/app/${appId}`,
  /* CLOUD MODE (default): View an app's orchestrations */
  orchestrations: (appId: string) => `${orchestrationBuilder}/app/${appId}/orchestrations`,
  /* View an app's orchestrations development mode */
  orchestrationsWithDevelopmentMode: (appId: string) =>
    `${orchestrationBuilder}/app/${appId}/development/orchestrations`,
  /* LOCAL DISK MODE: View a single orchestration */
  orchestrationLocalDiskMode: (appRefId: string, appId: string, flowId: string) => {
    return `${appBuilder}${buildLocalSuffix}/${appRefId}${orchestrationBuilder}/app/${appId}/development/orchestrations/${flowId}`;
  },
  /* LOCAL DISK MODE: View an app's orchestrations */
  orchestrationsLocalDiskMode: (appRefId: string, appId: string) => {
    return `${appBuilder}${buildLocalSuffix}/${appRefId}${orchestrationBuilder}/app/${appId}/development/orchestrations`;
  },
  help: '/help',
  logout: '/logout',
  /**
   * Returns the appropriate logout redirect URL based on user audience type.
   * Open Developer users are redirected to the digital domain SSO login page.
   * @param audience - The user's audience type (OPEN_DEVELOPER or ENTERPRISE)
   */
  logoutRedirect: (audience?: Audience) =>
    audience === Audience.OPEN_DEVELOPER ? `https://${DevsiteEnv.DIGITAL_DOMAIN}/en-us/logout.html?sso=opendev` : about,
  privacy: 'https://www.workday.com/en-us/privacy.html',
  profile: '/profile',
  retired: '/retired-functionality',
  search,
  searchFor: (q: string, filter?: string) =>
    `${search}?${filter ? `filter=${encodeURIComponent(filter)}&` : ''}${`q=${encodeURIComponent(q)}`}`,
  support: '/support',
  terms,
  termsAgree: `${terms}/agree`,
  termsHome: `${terms}/developerprogram`,
  termsOverview: `${terms}/overview`,
  termsOverviewHome: `${terms}/overview/developerprogram`,
  tutorials,
  console: consoleRoute,
  consoleAnalytics: consoleRoute + consoleRoutes.analytics,
  consoleAnalyticsPerformance: consoleRoute + consoleRoutes.analytics + '/performance',
  consoleAnalyticsLogs: consoleRoute + consoleRoutes.analytics + '/logs',
  consoleAnalyticsMetrics: consoleRoute + consoleRoutes.analytics + '/metrics',
  consoleAnalyticsApp: (appRefId: string) => consoleRoute + consoleRoutes.analytics + '/app/' + appRefId,
  consoleTenants: consoleRoute + consoleRoutes.tenants,
  consoleUsers: consoleRoute + consoleRoutes.users,
  consoleThirdPartyIntegrations: consoleRoute + '/third-party-integrations',
  consoleAccount: consoleRoute + consoleRoutes.account,
  consoleAccountEntitlements: consoleRoute + consoleRoutes.account + '/entitlements',
  consoleAccountThirdPartyIntegrations: consoleRoute + consoleRoutes.account + '/third-party-integrations',
  consoleAccountAudits: consoleRoute + consoleRoutes.account + '/audits',
  consoleAccountSaml: consoleRoute + consoleRoutes.account + '/saml',
  // console apps start (list all apps)
  consoleApps,
  // console apps my (list my apps)
  consoleAppsMyApps,
  // console built on workday apps
  consoleBuiltOnWorkdayApps,
  // console licensed aps
  consoleLicensedApps,
  // console retired apps
  consoleRetiredApps,
  // root route for when viewing a single app
  consoleAppRoot,
  // view a single app
  consoleAppView: (refId: string) => `${consoleAppRoot}/${refId}`,
  // view a single licensed app
  consoleAppLicensedView: (refId: string) => `${consoleAppLicensedRoot}/${refId}`,
  orchestrationLicensedActivity: (refId: string) => `${consoleAppLicensedRoot}/${refId}/orchestration-activity`,
  consoleAppLicensedLogs: (refId: string) => `${consoleAppLicensedRoot}/${refId}/logs`,
  orchestrationLicensedActivityRuns: (refId: string, orchId: string) =>
    `${consoleAppLicensedRoot}/${refId}/orchestration-activity/${orchId}`,
  // view activity
  consoleAppActivity: (refId: string) => `${consoleAppRoot}/${refId}/activity`,
  // view malt logs filtered to those relevant for the app
  consoleAppLogs: (refId: string) => `${consoleAppRoot}/${refId}/logs`,
  // view connectors for an app
  consoleAppConnectors: (refId: string) => `${consoleAppRoot}/${refId}/connectors`,
  // view orchestration dashboard
  orchestrationActivity: (refId: string) => `${consoleAppRoot}/${refId}/orchestration-activity`,
  // view orchestration dashboard for a specific orchestration
  orchestrationActivityRuns: (refId: string, orchId: string) =>
    `${consoleAppRoot}/${refId}/orchestration-activity/${orchId}`,
  // manage promotion and deployment
  consoleAppLifecycle: (refId: string) => `${consoleAppRoot}/${refId}/lifecycle`,
  // view a single version
  consoleAppVersionView: (refId: string, versionId: string) => `${consoleAppRoot}/${refId}/activity/${versionId}`,
  // view source for a single version
  consoleAppVersionSource: (refId: string, versionId: string) =>
    `${consoleAppRoot}/${refId}/activity/${versionId}/view`,
  // view a single build and the associated log
  consoleAppBuildLog: (refId: string, versionId: string, buildId: string) =>
    `${consoleAppRoot}/${refId}/activity/${versionId}/builds/${buildId}`,
  // view source for a single build
  consoleAppBuilderView: (refId: string, versionId: string, buildId: string) =>
    `${consoleAppRoot}/${refId}/activity/${versionId}/builds/${buildId}/view`,
  clients,
  clientsMyClients,
  clientsUnassigned,
  clientCreate: `${clients}/create`,
  clientView,
  clientEdit: (clientId: string) => clientView(clientId) + '/edit',
  appCatalog,
  appCatalogView: (appCatId: string) => `${appCatalog}/${appCatId}`,
  appCatalogAppBuilderView: (appCatId: string) => `${routes.appCatalogView(appCatId)}/code`,
  about,
  sandbox,
  welcome: '/welcome',
  login: '/login',
  loginPath: (path: string, platform?: Audience) =>
    `/login?path=${encodeURIComponent(path)}${platform ? `&platform=${platform}` : ''}`,
  forbidden: '/forbidden',
  error: '/error',
  root: '/',
  packageManagement,
  packageManagementPermissions,
  packageManagementView: (packageId: string) => `${packageManagement}/view/${packageId}`,
  packageManagementCreate: `${packageManagement}/create`,
  packageManagementEdit: (packageId: string) => `${packageManagement}/view/${packageId}/edit`,
  studiobuilder: '/studiobuilder',
  studiobuilderAuth: '/studiobuilder/auth',
  studiobuilderLandingView: (appId: string) => `/studiobuilder/app/${appId}`,
  studiobuilderCollection: (appId: string, collectionName: string) =>
    `/studiobuilder/app/${appId}/collection/${collectionName}/builder`,
  accessPolicies,
  accessPoliciesProducts,
  accessPoliciesProduct: (productName: string) => `${accessPoliciesProducts}/${productName}`,
  accessPoliciesProductAppliedCompanies: (productName: string) => `${accessPoliciesProducts}/${productName}/companies`,
  accessPoliciesProductFeatures: (productName: string) => `${accessPoliciesProducts}/${productName}/features`,
  accessPoliciesProductPermissions: (productName: string) => `${accessPoliciesProducts}/${productName}/permissions`,
  accessPoliciesFeatures,
  accessPoliciesFeature: (featureName: string) => `${accessPoliciesFeatures}/${featureName}`,
  accessPoliciesPackages,
  accessPoliciesPermissions,
};

/** Includes the Community namespace without matching unrelated path prefixes. */
export const isCommunityPath = (pathname: string): boolean =>
  pathname === community || pathname.startsWith(`${community}/`);

export default routes;
